import {
	Box,
	CircularProgress,
	Stack,
	Badge,
	Alert,
	AlertTitle,
	Typography,
	Button,
} from "@mui/material";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import type { GridColDef, GridRowsProp, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { useGetMyProjectTimeEntriesLazyQuery } from "@repo/server-api";
import { useUpdateMyDayProjectTimeEntryMutation } from "@repo/server-api";
import type { ProjectTimeEntry } from "@repo/server-api";
import { ProjectTimeEntryStatus } from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ExpandLess, ExpandMore, Email, Close, Save } from "@mui/icons-material";

import { DayDetailPanel } from "./DayDetailPanel";
import type { DayBasedTimeTrackingProps, NewTimeEntry } from "./types";
import { getDaysInMonth, validateTimeEntry } from "./utils";

import { formatDateTimeAsUtc, formatHoursMinutes } from "@repo/legacy/components/helper/Index";
import { formatDate } from "@repo/legacy/components/helper/Index";

export type DayRowData = {
	id: string; // date as "YYYY-MM-DD"
	date: string;
	dayOfWeek: string;
	entries: ProjectTimeEntry[];
	workingMinutes: number;
	approvedWorkingMinutes: number;
	pendingWorkingMinutes: number;
	rejectedWorkingMinutes: number;
	requiredWorkingMinutes: number;
	status: string;
	start?: string;
	end?: string;
	absenceReason?: string;
	items?: ProjectTimeEntry[];
};

export const DayBasedTimeTracking = ({
	projectId,
	projectPositions,
	timeTrackingResolution,
	projectStartDate,
	projectEndDate,
	isProjectInvalid,
	projectLeaderName,
	projectLeaderEmail,
	selectedMonth,
	onProjectSummariesRefresh,
}: DayBasedTimeTrackingProps) => {
	const { t, i18n } = useTranslation();
	const apiRef = useGridApiRef();
	const { showNotification } = useNotification();

	// Time entries by day
	const [dayGroups, setDayGroups] = useState<any[]>([]);

	// Changed entries per day (unsaved modifications)
	const [changedEntries, setChangedEntries] = useState<
		Map<string, (ProjectTimeEntry | NewTimeEntry)[]>
	>(new Map());

	// Preview data for real-time grid updates (before save)
	type PreviewData = {
		entries: any[];
		workingMinutes: number;
		start: string;
		end: string;
		status: string;
	};
	const [previewData, setPreviewData] = useState<Map<string, PreviewData>>(new Map());

	// Saving state
	const [saving, setSaving] = useState(false);

	const hasChanges = changedEntries.size > 0;

	// GraphQL Query
	const [getTimeEntries, { loading }] = useGetMyProjectTimeEntriesLazyQuery({
		fetchPolicy: "no-cache",
	});

	// GraphQL Mutation (moved from DayDetailPanel)
	const [updateMutation] = useUpdateMyDayProjectTimeEntryMutation({
		fetchPolicy: "no-cache",
	});

	// Load data when month or projectId changes
	useEffect(() => {
		handleRefresh();
		// Clear unsaved changes on navigation
		setChangedEntries(new Map());
		setPreviewData(new Map());
	}, [selectedMonth, projectId, getTimeEntries]);

	// Handler: Refresh data
	const handleRefresh = () => {
		const startDate = dayjs()
			.year(selectedMonth.year)
			.month(selectedMonth.month - 1)
			.startOf("month")
			.format("YYYY-MM-DD");

		const endDate = dayjs()
			.year(selectedMonth.year)
			.month(selectedMonth.month - 1)
			.endOf("month")
			.format("YYYY-MM-DD");

		getTimeEntries({ variables: { startDate, endDate, projectId } }).then((res) => {
			if (res.data && res.data.myProjectTimeEntries) {
				setDayGroups(res.data.myProjectTimeEntries as any[]);
			}
		});
	};

	// Helper: Calculate preview data from entries
	const calculatePreviewData = useCallback(
		(date: string, entries: (ProjectTimeEntry | NewTimeEntry)[]): PreviewData | null => {
			if (entries.length === 0) {
				return null;
			}

			const start = formatDateTimeAsUtc(
				entries.reduce((earliest, entry) => {
					const entryStart = dayjs(entry.start);
					return earliest.isBefore(entryStart) ? earliest : entryStart;
				}, dayjs(date).endOf("day")),
			);

			const end = formatDateTimeAsUtc(
				entries.reduce((latest, entry) => {
					const entryEnd = dayjs(entry.end);
					return latest.isAfter(entryEnd) ? latest : entryEnd;
				}, dayjs(date).startOf("day")),
			);

			const workingMinutes = entries.reduce((total, entry) => {
				const s = dayjs(entry.start);
				const e = dayjs(entry.end);
				return total + e.diff(s, "minute");
			}, 0);

			const status = entries.some((e) => e.status === "OPEN")
				? "OPEN"
				: entries.some((e) => e.status === "PENDING")
					? "PENDING"
					: entries.every((e) => e.status === "APPROVED")
						? "APPROVED"
						: entries.every((e) => e.status === "REJECTED")
							? "REJECTED"
							: "OPEN";

			return { entries, workingMinutes, start, end, status };
		},
		[],
	);

	// Handler: Entries changed in a DayDetailPanel
	const handleEntriesChange = useCallback(
		(date: string, entries: (ProjectTimeEntry | NewTimeEntry)[]) => {
			setChangedEntries((prev) => {
				const newMap = new Map(prev);
				newMap.set(date, entries);
				return newMap;
			});

			// Update preview data for real-time grid display
			const preview = calculatePreviewData(date, entries);
			setPreviewData((prev) => {
				const newMap = new Map(prev);
				if (preview === null) {
					newMap.delete(date);
				} else {
					newMap.set(date, preview);
				}
				return newMap;
			});
		},
		[calculatePreviewData],
	);

	// Handler: Save all changed days
	const handleSaveAll = async () => {
		if (changedEntries.size === 0) return;

		// Validate all changed days (before setting saving state)
		for (const [date, dayEntries] of Array.from(changedEntries.entries())) {
			const errors = dayEntries.map((e) => validateTimeEntry(e)).filter((e): e is string => e !== null);
			if (errors.length > 0) {
				showNotification(
					`${dayjs(date).format("DD.MM.YYYY")}: ${t(errors[0]!)}`,
					"error",
				);
				return;
			}
		}

		setSaving(true);

		try {
			const results = await Promise.allSettled(
				Array.from(changedEntries.entries()).map(async ([date, entries]) => {
					const timeEntries = entries.map((e) => ({
						projectTimeEntryId: e.id.startsWith("new") ? undefined : e.id,
						start: formatDateTimeAsUtc(e.start),
						end: formatDateTimeAsUtc(e.end),
						projectPositionId: e.projectPositionId,
						description: e.description || "",
					}));

					const result = await updateMutation({
						variables: {
							date: dayjs(date).format("YYYY-MM-DD"),
							timeEntries,
							projectId,
						},
					});

					// Check for mutation-level errors
					const payload = result.data?.updateMyDayProjectTimeEntry;
					if (payload?.errors?.length) {
						const errorMessages = payload.errors
							.map((error) => ("message" in error && error.message ? error.message : ""))
							.filter(Boolean);
						throw new Error(errorMessages.join(", ") || "Unknown error");
					}

					// Check for day group level errors
					if (payload?.projectTimeEntryDayGroup?.errors?.length) {
						throw new Error(payload.projectTimeEntryDayGroup.errors.join(", "));
					}

					return { date, dayGroup: payload?.projectTimeEntryDayGroup };
				}),
			);

			// Process results
			const succeeded: string[] = [];
			const failed: string[] = [];

			for (const result of results) {
				if (result.status === "fulfilled" && result.value.dayGroup) {
					const { date, dayGroup } = result.value;
					succeeded.push(date);

					// Update dayGroups with saved data
					setDayGroups((prevDayGroups) => {
						const existingIndex = prevDayGroups.findIndex((dg) => dg.date === dayGroup.date);
						if (existingIndex >= 0) {
							const newDayGroups = [...prevDayGroups];
							newDayGroups[existingIndex] = dayGroup;
							return newDayGroups;
						}
						return [...prevDayGroups, dayGroup];
					});
				} else {
					// Extract date from the failed result
					const idx = results.indexOf(result);
					const dateKey = Array.from(changedEntries.keys())[idx]!;
					failed.push(dateKey);
					if (result.status === "rejected") {
						showNotification(
							`${dayjs(dateKey).format("DD.MM.YYYY")}: ${result.reason instanceof Error ? result.reason.message : "Error"}`,
							"error",
						);
					}
				}
			}

			// Remove succeeded days from changedEntries and previewData
			if (succeeded.length > 0) {
				setChangedEntries((prev) => {
					const newMap = new Map(prev);
					for (const date of succeeded) {
						newMap.delete(date);
					}
					return newMap;
				});
				setPreviewData((prev) => {
					const newMap = new Map(prev);
					for (const date of succeeded) {
						newMap.delete(date);
					}
					return newMap;
				});
			}

			// Show notification
			if (failed.length === 0) {
				showNotification(t("save-success"), "success");
			} else if (succeeded.length > 0) {
				showNotification(t("save-partial-success"), "warning");
			}

			onProjectSummariesRefresh?.();
		} finally {
			setSaving(false);
		}
	};

	// Handler: Cancel all changes
	const handleCancelAll = () => {
		setChangedEntries(new Map());
		setPreviewData(new Map());
	};

	// Convert day groups to DataGrid rows
	const rows: GridRowsProp<DayRowData> = useMemo(() => {
		let allDays = getDaysInMonth(selectedMonth.year, selectedMonth.month);
		const dayGroupMap = new Map(dayGroups.map((dg) => [dg.date, dg]));

		allDays = allDays.filter((date) => {
			const dayObj = dayjs(date);
			const projectStart = dayjs(projectStartDate);
			const projectEnd = dayjs(projectEndDate);
			return (
				(dayObj.isAfter(projectStart) || dayObj.isSame(projectStart)) &&
				(dayObj.isBefore(projectEnd) || dayObj.isSame(projectEnd))
			);
		});

		return allDays.map((date) => {
			const dayGroup = dayGroupMap.get(date);
			const dayObj = dayjs(date);
			const preview = previewData.get(date);

			// If we have preview data, use it for real-time display
			// BUT: Keep entries as original server data to avoid refresh loops in DayDetailPanel
			if (preview) {
				return {
					id: date,
					date,
					dayOfWeek: dayObj.format("dddd"),
					entries: dayGroup?.items || [], // Always use original server data
					workingMinutes: preview.workingMinutes,
					approvedWorkingMinutes: dayGroup?.approvedWorkingMinutes || 0,
					pendingWorkingMinutes: dayGroup?.pendingWorkingMinutes || 0,
					rejectedWorkingMinutes: dayGroup?.rejectedWorkingMinutes || 0,
					requiredWorkingMinutes: dayGroup?.requiredWorkingMinutes || 0,
					status: preview.status,
					start: preview.start,
					end: preview.end,
					absenceReason: dayGroup?.absenceReason,
				};
			}

			// Otherwise use server data (existing logic)
			let dayGroupStart = formatDateTimeAsUtc(
				dayGroup?.items?.reduce((acc: dayjs.Dayjs, child: ProjectTimeEntry) => {
					const childStart = dayjs(child.start);
					return acc.isBefore(childStart) ? acc : childStart;
				}, dayjs(dayGroup.date).endOf("day")),
			);

			let dayGroupEnd = formatDateTimeAsUtc(
				dayGroup?.items?.reduce((acc: dayjs.Dayjs, child: ProjectTimeEntry) => {
					const childEnd = dayjs(child.end);
					return acc.isAfter(childEnd) ? acc : childEnd;
				}, dayjs(dayGroup.date).startOf("day")),
			);

			if (dayjs(dayGroupStart).isAfter(dayjs(dayGroupEnd))) {
				dayGroupStart = dayjs(dayGroupEnd).startOf("day").format("YYYY-MM-DD HH:mm");
				dayGroupEnd = dayjs(dayGroupStart).startOf("day").format("YYYY-MM-DD HH:mm");
			}

			const dayGroupStatus = dayGroup?.items?.some(
				(child: ProjectTimeEntry) => child?.status === ProjectTimeEntryStatus.Open,
			)
				? ProjectTimeEntryStatus.Open
				: dayGroup?.items?.some(
							(child: ProjectTimeEntry) => child?.status === ProjectTimeEntryStatus.Pending,
					  )
					? ProjectTimeEntryStatus.Pending
					: dayGroup?.items?.every(
								(child: ProjectTimeEntry) => child?.status === ProjectTimeEntryStatus.Approved,
						  )
						? ProjectTimeEntryStatus.Approved
						: dayGroup?.items?.every(
									(child: ProjectTimeEntry) => child?.status === ProjectTimeEntryStatus.Rejected,
							  )
							? ProjectTimeEntryStatus.Rejected
							: ProjectTimeEntryStatus.Open;

			const dayWorkingMinutes = dayGroup?.items?.reduce((acc: number, child: ProjectTimeEntry) => {
				const start = dayjs(child.start);
				const end = dayjs(child.end);
				return acc + end.diff(start, "minute");
			}, 0);

			return {
				id: date,
				date,
				dayOfWeek: dayObj.format("dddd"),
				entries: dayGroup?.items || [],
				workingMinutes: dayWorkingMinutes || 0,
				approvedWorkingMinutes: dayGroup?.approvedWorkingMinutes || 0,
				pendingWorkingMinutes: dayGroup?.pendingWorkingMinutes || 0,
				rejectedWorkingMinutes: dayGroup?.rejectedWorkingMinutes || 0,
				requiredWorkingMinutes: dayGroup?.requiredWorkingMinutes || 0,
				status: dayGroupStatus,
				start: dayGroupStart,
				end: dayGroupEnd,
				absenceReason: dayGroup?.absenceReason,
			};
		});
	}, [dayGroups, selectedMonth, previewData, projectStartDate, projectEndDate]);

	// DataGrid columns
	const columns: GridColDef<DayRowData>[] = useMemo(
		() => [
			{
				field: "date",
				headerName: t("date"),
				width: 150,
				headerAlign: "left",
				renderCell: (params: GridRenderCellParams<DayRowData>) => {
					const date = dayjs(params.row.date);
					const dayAbbr = t("day-of-week." + params.row.dayOfWeek.toLowerCase(), { ns: "enum" }).slice(0, 2);
					return (
						<div
							style={{
								justifyContent: "left",
								alignItems: "center",
								display: "flex",
								height: "100%",
							}}>
							<span style={{ display: "inline-block", width: "24px" }}>{dayAbbr}</span>
							{formatDate(date, i18n.language)}
						</div>
					);
				},
			},
			{
				field: "startEnd",
				headerName: t("start-till-end"),
				flex: 1,
				minWidth: 150,
				sortable: false,
				headerAlign: "center",
				renderCell: (params: GridRenderCellParams<DayRowData>) => {
					const start = dayjs(params.row.start).utc().format("HH:mm");
					const end = dayjs(params.row.end).utc().format("HH:mm");
					let text = "";
					const absenceReason = params.row.absenceReason ?? "";

					if (start !== end) {
						text = `${start} → ${end} ${absenceReason}`;
					} else {
						text = absenceReason;
					}

					return (
						<div
							style={{
								justifyContent: "center",
								alignItems: "center",
								display: "flex",
								height: "100%",
							}}>
							{text}
						</div>
					);
				},
			},
			{
				field: "workingMinutes",
				headerName: t("worked-hours"),
				width: 180,
				headerAlign: "center",
				renderCell: (params: GridRenderCellParams<DayRowData>) => {
					return (
						<div
							style={{
								justifyContent: "center",
								alignItems: "center",
								display: "flex",
								height: "100%",
							}}>
							{formatHoursMinutes(params.row.workingMinutes)}
						</div>
					);
				},
			},
			{
				field: "status",
				headerName: t("status"),
				width: 150,
				headerAlign: "center",
				renderCell: (params: GridRenderCellParams<DayRowData>) => {
					if (params.row.entries?.length === 0) return null;
					let fontColor = "#000";
					let bgColor = "#f9a825";

					switch (params.row.status) {
						case "APPROVED":
							fontColor = "#fff";
							bgColor = "#4caf50";
							break;
						case "REJECTED":
							fontColor = "#000";
							bgColor = "#f44336";
							break;
						case "PENDING":
							fontColor = "#000";
							bgColor = "#f9a825";
							break;
						case "CANCELED":
							fontColor = "#000";
							bgColor = "#9e9e9e";
							break;
						case "OPEN":
							fontColor = "#000";
							bgColor = "lightblue";
							break;
						default:
							fontColor = "#000";
							bgColor = "lightblue";
							break;
					}
					return (
						<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
						<Badge
							style={{
								justifyContent: "center",
								alignItems: "center",
								display: "flex",
								backgroundColor: bgColor,
								color: fontColor,
								borderRadius: "10px",
								padding: "2px 8px",
								fontSize: "12px",
								fontWeight: "bold",
								height: "15px",
							}}>
							{t("time-entry.status." + params.row.status.toLowerCase(), {
								ns: "enum",
							})}
						</Badge>
						</div>
					);
				},
			},
			],
		[t, i18n.language, rows],
	);

	// Detail panel component
	const getDetailPanelContent = useCallback(
		({ row }: { row: DayRowData }) => (
			<DayDetailPanel
				date={row.date}
				entries={row.entries}
				projectPositions={projectPositions}
				timeTrackingResolution={timeTrackingResolution}
				projectId={projectId}
				changedEntries={changedEntries.get(row.date)}
				onEntriesChange={handleEntriesChange}
				saving={saving}
			/>
		),
		[projectPositions, timeTrackingResolution, projectId, changedEntries, handleEntriesChange, saving],
	);

	// Auto-expand rows
	const getDetailPanelHeight = useCallback(() => "auto", []);

	return (
		<Stack spacing={1} sx={{ height: "100%", width: "100%", flex: 1, minHeight: 0 }}>
			{/* Project not configured error */}
			{isProjectInvalid ? (
				<Alert severity="warning" sx={{ mt: 2 }}>
					<AlertTitle>{t("project-not-configured-title")}</AlertTitle>
					{t("project-not-configured-message")}
					{projectLeaderName && (
						<Box sx={{ mt: 1 }}>
							<Typography variant="body2">
								{t("project-leader")}: {projectLeaderName}
							</Typography>
							{projectLeaderEmail && (
								<Button
									href={`mailto:${projectLeaderEmail}`}
									startIcon={<Email />}
									size="small"
									sx={{ mt: 1 }}>
									{t("send-email")}
								</Button>
							)}
						</Box>
					)}
				</Alert>
			) : loading ? (
				/* Loading State */
				<Box display="flex" justifyContent="center" py={5}>
					<CircularProgress />
				</Box>
			) : rows.length === 0 ? (
				<Box sx={{ p: 3, textAlign: "center" }}>{t("no-time-entries-for-this-month")}</Box>
			) : (
				/* DataGrid */
				<Box sx={{ flex: 1, minHeight: 300, width: "100%" }}>
					<DataGridPremium
						apiRef={apiRef}
						getRowHeight={() => "auto"}
						rows={rows}
						columns={columns}
						getDetailPanelContent={getDetailPanelContent}
						getDetailPanelHeight={getDetailPanelHeight}
						disableRowSelectionOnClick
						density="compact"
						localeText={{
							noRowsLabel: t("no-data-available"),
						}}
						sx={{
							"& .MuiDataGrid-detailPanel": {
								backgroundColor: "background.default",
							},
							"& .MuiDataGrid-detailPanelContent": {
								width: "100%",
							},
						}}
						slots={{
							detailPanelCollapseIcon: ExpandLess,
							detailPanelExpandIcon: ExpandMore,
						}}
					/>
					{/* Global Save/Cancel Bar — sticky at bottom */}
					{hasChanges && (
						<Stack
							direction="row"
							spacing={2}
							justifyContent="flex-end"
							sx={{
								position: "sticky",
								bottom: 0,
								zIndex: 10,
								py: 1.5,
								px: 2,
								backgroundColor: "background.paper",
								borderTop: 1,
								borderColor: "divider",
								boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
							}}>
							<Button
								variant="outlined"
								startIcon={<Close />}
								onClick={handleCancelAll}
								disabled={saving}>
								{t("cancel")}
							</Button>
							<Button
								variant="contained"
								startIcon={saving ? <CircularProgress size={20} /> : <Save />}
								onClick={handleSaveAll}
								disabled={saving}
								data-testid="save-all-button">
								{saving ? t("saving") : t("save")}
							</Button>
						</Stack>
					)}
				</Box>
			)}
		</Stack>
	);
};
